import { isEqual } from 'lodash';

export function filterObject<Obj extends object>(obj: Obj, filterFn: (value, key) => boolean): Obj {
	return Object.entries(obj).reduce((cleanObject, [key, value]) => {
		if (filterFn(value, key)) {
			cleanObject[key] = value;
		}

		return cleanObject;
	}, {} as Obj);
}

/**
 * Deep diff between two objects, using lodash.
 * Important note: array changes are treated atomically, not recursively, e.g. diffing `[1]` and `[2]` will result in `[2]` and not `{ '0': 2 }`
 * @param first - The first object to compare
 * @param second - The second object to compare
 * @returns A new object representing the difference between the two objects
 */
export function objectDiff(first: Record<string, unknown>, second: Record<string, unknown>): Record<string, unknown> {
	if (first === null) {
		return second;
	}

	if (first === second) {
		return {};
	}

	return Object.keys(second).reduce((result, key) => {
		const firstValue = first[key];
		const secondValue = second[key];

		if (firstValue === secondValue) {
			return result;
		}

		if (isObjectLike(firstValue) && isObjectLike(secondValue)) {
			const nestedDiff = objectDiff(firstValue, secondValue);
			if (Object.keys(nestedDiff).length > 0) {
				result[key] = nestedDiff;
			}
		} else if (!isEqual(firstValue, secondValue)) {
			result[key] = secondValue;
		}

		return result;
	}, {});
}

export function flattenObject(obj: Record<string, any>) {
	const toReturn = {};

	for (const key of Object.keys(obj)) {
		if (obj[key] === null || (Array.isArray(obj[key]) && obj[key].length === 0) || typeof obj[key] !== 'object') {
			toReturn[key] = obj[key];
		} else {
			const flatObject = flattenObject(obj[key]);

			for (const flatObjectKey of Object.keys(flatObject)) {
				toReturn[key + '.' + flatObjectKey] = flatObject[flatObjectKey];
			}
		}
	}
	return toReturn;
}

export const isObjectLike = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);
